import api from '@/api/services/auth'
import Cookies from 'js-cookie'

const loadProfileHistories = () => {
  const stored = localStorage.getItem('profileHistory')
  if (!stored) {
    return []
  }
  try {
    const parsed = JSON.parse(stored)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

const syncProfileHistory = (payload, loginId) => {
  const histories = loadProfileHistories()
  const existing = histories.find(h => h.loginId === loginId)
  if (existing) {
    existing.profile = payload
  } else {
    histories.push({ loginId: loginId, profile: payload })
  }
  localStorage.setItem('profileHistory', JSON.stringify(histories))
}

// 세션 이상(오염 제조/전파) 관측 지점 공용 — 콘솔에 항상 남기고, Sentry 가 로드돼 있으면 원격으로도 보낸다.
// "로그인 상태인데 refresh_token 없음" 세션은 access 만료(30분) 시 강제 메인행이 되는 오염 상태
// Sentry npm 도입 시 main.js 에서 window.Sentry = Sentry 로 노출하거나 이 함수 내부만 교체하면 됨.
// 주의: extra 에 토큰 원문 금지 (iat/exp 등 메타데이터만)
export function reportAuthAnomaly (message, extra) {
  console.error(message, extra)
  const S = globalThis.Sentry
  if (S && typeof S.captureMessage === 'function') {
    S.captureMessage(message, { level: 'error', extra: extra })
  }
}

export default {
  namespaced: true,
  state: {
    auth: {
      userId: '',
      orgUserId: '',
      userName: '',
      userEname: '',
      userType: '',
      cstCd: '',
      role: '',
      serviceLang: 'KOR',
      login: false
    },
    savedUserId: localStorage.getItem('savedUserId') ?? '',
    serviceLang: 'KOR',
    serviceCtrCd: '',
    jwt: '',
    requireLogin: false,
    requireLoginRtnPath: '',
    savedProfileList: [],
    selectedProfile: {},
    // 비로그인에서도 항상 빈 객체 보장 — undefined 면 템플릿의 memberDetail.xxx 참조가
    // 렌더 크래시를 일으킴 (중국어 브라우저 + 비로그인 DEM/DET 빈 화면 이슈, 2026-07)
    memberDetail: {},
    compName: '',
    loginResp: [],
    calcEiCatCd: '',
    // ekmtc_admin_login: false
    isEmptyProfileLogin: false
  },
  actions: {
    init ({ commit, state, dispatch }, router) {
      // [자가 치유] 오염 세션(로그인+refresh 없음)은 부팅 시점에 조용히 정리 — access 만료 후 강제 메인행 방지.
      // 정리 후 아래 getSessionStorage 브로드캐스트가 다른 탭의 정상 세션을 인계받으면 자연 복구되고,
      // 없으면 정상적인 비로그인 화면이 된다.
      if (sessionStorage.getItem('auth') && !sessionStorage.getItem('refresh_token')) {
        reportAuthAnomaly('[AUTH] boot with auth but no refresh_token - clearing contaminated session', {
          hash: location.hash,
          isPopup: !!globalThis.opener
        })
        dispatch('delLoginInfo', { quiet: true })
      }
      globalThis.addEventListener('storage', (event) => {
        if (!event.newValue) return
        if (event.key === 'getSessionStorage') {
          // 로그아웃이 로그인보다 최근이면(로그아웃 진행 중) 로그인 상태를 재공유하지 않음 → 멀티탭 재로그인 경쟁 방지
          const loginTime = Number(localStorage.getItem('loginTime')) || 0
          const logoutTime = Number(localStorage.getItem('logoutTime')) || 0
          if (state.jwt && loginTime > logoutTime) {
            localStorage.setItem('sessionStorage', JSON.stringify({
              state: state,
              refreshToken: sessionStorage.getItem('refresh_token'),
              cstCd: sessionStorage.getItem('cstCd'),
              loginId: sessionStorage.getItem('loginId')
            }))
            localStorage.removeItem('sessionStorage')
          }
        } else if (event.key === 'delSessionStorage') {
          if (!state.jwt) return // 이미 로그아웃된 탭은 무시 → 재진입/무한 '/' 루프 차단
          dispatch('delLoginInfo')
          globalThis.location.href = '/' // 로그인 상태였던 탭만 1회 메인 이동
        } else if (event.key === 'tokenSync') {
          // 다른 탭에서 /refresh 로 회전된 토큰 수신 → 조용히 교체 (reload 불필요)
          if (!state.jwt) return // 로그아웃 상태 탭에는 적용하지 않음
          const t = JSON.parse(event.newValue)
          sessionStorage.setItem('access_token', t.access)
          if (t.refresh) {
            sessionStorage.setItem('refresh_token', t.refresh)
          }
          commit('jwt', t.access)
        } else if (event.key === 'sessionStorage') {
          // 로그아웃이 더 최근이면 로그인 복원 안 함 (로그아웃 ↔ 복원 경쟁 방지)
          const loginTime = Number(localStorage.getItem('loginTime')) || 0
          const logoutTime = Number(localStorage.getItem('logoutTime')) || 0
          if (loginTime <= logoutTime) return

          const sess = JSON.parse(event.newValue)
          const newState = sess.state

          // 로그아웃 상태인 창만 로그인 복원(+reload). 이미 로그인된 창은 토큰 회전으로 jwt 가
          // 달라도 리로드하지 않음(회전 갱신은 tokenSync 가 담당) → 멀티창 불필요한 재로그인/리로드 방지
          if (!state.jwt) {
            // [전파 차단] refresh 없는 세션은 인계받지 않음 — 30분 시한부 로그인(강제 메인행 폭탄)만 만들므로 거부.
            // 거부해도 다른 정상 탭의 응답(별도 storage 이벤트)이 오면 그때 정상 복원된다.
            if (!sess.refreshToken) {
              reportAuthAnomaly('[AUTH] session handover without refreshToken - restore refused', {
                hash: location.hash,
                isPopup: !!globalThis.opener
              })
              return
            }
            sessionStorage.setItem('access_token', newState.jwt)
            sessionStorage.setItem('refresh_token', sess.refreshToken)

            sessionStorage.setItem('auth', JSON.stringify({
              auth: newState.auth,
              memberDetail: newState.memberDetail,
              jwt: newState.jwt
            }))
            localStorage.setItem('service_lang', newState.auth.serviceLang)
            localStorage.setItem('service_ctrcd', newState.auth.userCtrCd)
            sessionStorage.setItem('cstCd', sess.cstCd)
            sessionStorage.setItem('loginId', sess.loginId)

            commit('auth', newState.auth)
            commit('memberDetail', newState.memberDetail)
            commit('jwt', newState.jwt)
            commit('selectedProfile', newState.selectedProfile)
            commit('loginInfo', newState.loginInfo)
            commit('compName', newState.compName)
            commit('serviceLang', newState.auth.serviceLang)
            commit('serviceCtrCd', newState.auth.userCtrCd)
            commit('requireLogin', false)

            // 이미 열려있던 탭도 로그인 상태로 레이아웃·본문 전체 재렌더
            if (state.requireLoginRtnPath) {
              router.push(state.requireLoginRtnPath)
              commit('requireLoginRtnPath', '')
            } else {
              globalThis.location.reload()
            }
          }
        }
      })

      localStorage.setItem('getSessionStorage', 'init')
      localStorage.removeItem('getSessionStorage')
    },
    saveLoginAdmin ({ commit, dispatch }, { user, jwt, refreshToken = '' }) {
      console.log('@@@@ enter saveLoginAdmin')
      dispatch('delLoginInfo', { quiet: true }) // 로그인 절차 내 정리 — logoutTime 기록 금지(다른 탭 로그아웃 방지)

      sessionStorage.setItem('access_token', jwt)
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken)
      } else {
        // [오염원 추적] refresh 없이 로그인 세션 저장 = 오염 제조 순간. 스택으로 호출 경로를 남긴다.
        reportAuthAnomaly('[AUTH] saveLoginAdmin without refreshToken', {
          stack: new Error().stack,
          hash: location.hash,
          isPopup: !!globalThis.opener
        })
      }

      return new Promise((resolve, reject) => {
        const auth = {
          auth: user,
          jwt: jwt
        }

        sessionStorage.setItem('admin_auth', JSON.stringify(auth))
        localStorage.setItem('service_lang', user.serviceLang)
        localStorage.setItem('service_ctrcd', user.userCtrCd)

        commit('auth', user)
        commit('serviceLang', user.serviceLang)
        commit('serviceCtrCd', user.userCtrCd)
        commit('jwt', jwt)
        commit('requireLogin', false)
        localStorage.setItem('loginTime', String(Date.now())) // 멀티탭: 최근 인증 이벤트=로그인
        localStorage.setItem('lastActivityTime', String(Date.now())) // 유휴 타이머 리셋 (옛 기록으로 즉시 로그아웃 방지)
        resolve(auth)
      })
    },
    saveLogin ({ commit, dispatch, state }, { user, jwt, refreshToken = '', savedUserId = '', profile, loginInfo }) {
      dispatch('delLoginInfo', { quiet: true }) // 로그인 절차 내 정리 — logoutTime 기록 금지(프로필 전환 중 다른 탭 로그아웃 방지)

      if (savedUserId) {
        localStorage.setItem('savedUserId', savedUserId)
        commit('savedUserId', savedUserId)
      } else {
        localStorage.removeItem('savedUserId')
        commit('savedUserId', '')
      }

      Cookies.set('access_token', jwt, { path: '/' })

      sessionStorage.setItem('access_token', jwt)
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken)
      } else {
        // [오염원 추적] refresh 없이 로그인 세션 저장 = 오염 제조 순간. 스택으로 호출 경로를 남긴다.
        reportAuthAnomaly('[AUTH] saveLogin without refreshToken', {
          stack: new Error().stack,
          hash: location.hash,
          isPopup: !!globalThis.opener
        })
      }
      user.login = true

      return new Promise((resolve, reject) => {
        api.info(jwt)
          .then(infoResp => {
            if (infoResp.headers.respcode === 'C0000') {
              const memberDetail = infoResp.data
              delete memberDetail.compAddr
              //delete memberDetail.compEname
              //delete memberDetail.compName
              delete memberDetail.fax1
              delete memberDetail.fax2
              delete memberDetail.fax3
              delete memberDetail.recvMailAddr1
              delete memberDetail.recvMailAddr2
              delete memberDetail.tel1
              delete memberDetail.tel2
              delete memberDetail.tel3
              delete memberDetail.tel4
              delete memberDetail.userEmail
              delete memberDetail.userEname
              delete memberDetail.userId
              delete memberDetail.userName

              const auth = {
                auth: user,
                memberDetail: memberDetail,
                jwt: jwt
              }

              sessionStorage.setItem('auth', JSON.stringify(auth))
              localStorage.setItem('service_lang', user.serviceLang)
              localStorage.setItem('service_ctrcd', user.userCtrCd)

              commit('auth', user)
              commit('memberDetail', memberDetail)
              commit('serviceLang', user.serviceLang)
              commit('serviceCtrCd', user.userCtrCd)
              commit('jwt', jwt)
              commit('requireLogin', false)
              commit('selectedProfile', profile)
              commit('loginInfo', loginInfo)
              commit('updateCalcEiCatCd', '')

              // 이미 열려있는 다른 탭에도 로그인 전파(수신 핸들러가 복원 + 재렌더)
              localStorage.setItem('loginTime', String(Date.now())) // 멀티탭: 최근 인증 이벤트=로그인
              localStorage.setItem('lastActivityTime', String(Date.now())) // 유휴 타이머 리셋 (옛 기록으로 즉시 로그아웃 방지)
              localStorage.setItem('sessionStorage', JSON.stringify({
                state: state,
                refreshToken: sessionStorage.getItem('refresh_token'),
                cstCd: sessionStorage.getItem('cstCd'),
                loginId: sessionStorage.getItem('loginId')
              }))
              localStorage.removeItem('sessionStorage')

              resolve(auth)
            } else {
              reject(infoResp)
            }
          })
      })
    },
    updateServiceLang ({ commit, state }, { serviceLang, serviceCtrCd }) {
      commit('serviceLang', serviceLang)
      commit('serviceCtrCd', serviceCtrCd)
      localStorage.setItem('service_lang', serviceLang)
      localStorage.setItem('service_ctrcd', serviceCtrCd)
    },
    delLoginInfo ({ commit, state }, payload) {
      const quiet = !!(payload && payload.quiet) // stale 세션 로컬 정리용: logoutTime 기록 안 함(다른 정상 탭 로그아웃 방지)
      const hadJwt = !!state.jwt // 클리어 전 실제 로그인 상태였는지
      let serviceLang = state.auth.serviceLang

      commit('auth', {
        userId: '',
        userName: '',
        userEname: '',
        userType: '',
        cstCd: '',
        role: '',
        serviceLang: serviceLang,
        login: false
      })
      commit('memberDetail', {})
      commit('jwt', '')
      commit('selectedProfile', {})
      commit('loginInfo', {})
      commit('updateCalcEiCatCd', '')
      // commit('updateIsEmptyProfileLogin', false)

      // commit('savedUserId', '')

      sessionStorage.removeItem('auth')
      sessionStorage.removeItem('admin_auth')
      // sessionStorage.removeItem('service_lang')
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      Cookies.remove('access_token', { path: '/' }) // 로그아웃 후 유효 access 가 쿠키에 잔존하지 않도록 제거
      if (hadJwt && !quiet) {
        // 로그인 상태였을 때만 기록 — 비로그인 상태의 delLoginInfo(새 창 초기화 등)가 logoutTime 을 오염시켜
        // 멀티탭 로그인 복원(경쟁 가드)을 막는 것을 방지
        localStorage.setItem('logoutTime', String(Date.now()))
      }
      // localStorage.removeItem('savedUserId')

      commit('user/clearManagerInfoCache', null, { root: true })
    },
    delLogin ({ commit, state, dispatch }) {
      const jwt = state.jwt

      dispatch('delLoginInfo')

      if (this.bc) {
        this.bc.postMessage(JSON.stringify({ cmd: 'del' }))
      }

      if (jwt) {
        return new Promise((resolve, reject) => {
          api.logout(jwt).then(() => {
            localStorage.setItem('delSessionStorage', 'del')
            localStorage.removeItem('delSessionStorage')
            // resolve()

            globalThis.location.href = '/'
          })
        })
      }

      return Promise.resolve()
    },
    bsaDelLogin ({ commit, state, dispatch }) {
      const jwt = state.jwt

      dispatch('delLoginInfo')

      if (this.bc) {
        this.bc.postMessage(JSON.stringify({ cmd: 'del' }))
      }

      if (jwt) {
        return new Promise((resolve, reject) => {
          api.logout(jwt).then(() => {
            localStorage.setItem('delSessionStorage', 'del')
            localStorage.removeItem('delSessionStorage')
            // resolve()

            globalThis.location.href = '/#/bsaMain'
          })
        })
      }

      return Promise.resolve()
    },
    delSaveUserId ({ commit, state, dispatch }) {
      commit('savedUserId', '')
      localStorage.removeItem('savedUserId')
    },
    saveProfileList ({ commit, state }, profileList) {
      commit('savedProfileList', profileList)
    }
  },
  getters: {
    auth: (state) => {
      return state.auth
    },
    isStaff: (state) => {
      return state.auth?.staffFlag === 'Y'
    },
    serviceLang: (state) => {
      return state.serviceLang
    },
    serviceCtrCd: (state) => {
      return state.serviceCtrCd
    },
    memberDetail: (state) => {
      return state.memberDetail
    },
    loginInfo: (state) => {
      return state.loginInfo
    },
    jwt: (state) => {
      return state.jwt
    },
    requireLogin: (state) => {
      return state.requireLogin
    },
    savedUserId: (state) => {
      return state.savedUserId
    },
    requireLoginRtnPath: (state) => {
      return state.requireLoginRtnPath
    },
    selectedProfile: (state) => {
      return state.selectedProfile
    },
    savedProfileList: (state) => {
      return state.savedProfileList
    },
    compName: (state) => {
      return state.compName
    },
    loginResp: (state) => {
      return state.compName
    },
    getCalcEiCatCd: (state) => {
      if (state.calcEiCatCd) {
        return state.calcEiCatCd
      } else {
        // 검색조건에 수입/수출이 변경되지 않아 Vuex에 없는 경우. default 값을 사용자 profile에서 읽는다
        if (state.selectedProfile) {
          // 'A' 수입/수출 전체인데 검색조건 세팅시 기본값 수출로 세팅
          return (state.selectedProfile.eiCatCd === 'A' ? 'O' : state.selectedProfile.eiCatCd)
        } else {
          return state.calcEiCatCd // 프로파일이 없으면 빈값 그대로 리턴
        }
      }
    },
    getIsEmptyProfileLogin: (state) => {
      return state.isEmptyProfileLogin
    }
  },
  mutations: {
    defaultAssign: (state, payload) => {
      const { key, value } = payload
      state.auth[key] = value
    },
    auth: (state, payload) => {
      state.auth = payload
    },
    serviceLang: (state, payload) => {
      if (state.auth) {
        state.auth.serviceLang = payload
      }

      state.serviceLang = payload
    },
    memberDetail: (state, payload) => {
      state.memberDetail = payload
    },
    loginInfo: (state, payload) => {
      state.loginInfo = payload
    },
    loginResp: (state, payload) => {
      state.loginInfo = payload
    },
    jwt: (state, payload) => {
      state.jwt = payload
    },
    requireLogin: (state, payload) => {
      if (state.jwt === '' || state.jwt === null) {
        state.requireLogin = payload
      } else {
        state.requireLogin = false
      }
    },
    savedUserId: (state, payload) => {
      state.savedUserId = payload
    },
    requireLoginRtnPath: (state, payload) => {
      state.requireLoginRtnPath = payload
    },
    selectedProfile: (state, payload) => {
      // if (payload != null && payload.picNo !== undefined) {
      if (payload?.picNo !== undefined) {
        const loginId = sessionStorage.getItem('loginId')
        syncProfileHistory(payload, loginId)
      }
      state.selectedProfile = payload
    },
    savedProfileList: (state, payload) => {
      state.savedProfileList = payload
    },
    savedCompName: (state, payload) => {
      state.compName = payload
    },
    updateCalcEiCatCd (state, payload) {
      state.calcEiCatCd = payload
    },
    updateIsEmptyProfileLogin (state, payload) {
      state.isEmptyProfileLogin = payload
    },
    serviceCtrCd (state, payload) {
      state.serviceCtrCd = payload
    }
  }
}
